import { SortDirection } from "client/Api/common";
import {
	AnnouncementPreviewItemModel,
	AnnouncementSortProperty,
	list as listAnnouncements
} from "client/Api/Hub/Announcements/announcementViewer";
import {
	EventDateFilterType,
	EventDateSortDirection,
	EventPreviewModel,
	list as listEvents
} from "client/Api/Hub/Events/eventViewer";
import {
	KnowledgeItemPreviewItemModel,
	KnowledgeItemSortProperty,
	list as listKnowledgeItems
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ComboBox,
	ComboBoxIntermediateState,
	FormItem,
	Highlight,
	SuggestedComboBoxItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createRemoteDataSource, useDebouncedValue } from "client/Hooks";
import { getHighlight } from "client/Misc/highlight";
import { generateTrackingId, isNilOrWhiteSpace } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement, useMemo, useState } from "react";
import { TimeZone } from "client/DateAndTime/timezones";
import { AnnouncementListItem } from "client/Features/Hub/Announcements/ListItem/AnnouncementListItem";
import { KnowledgeItemListItem } from "client/Features/Hub/KnowledgeItems/ListItem/KnowledgeItemListItem";
import { EventListItem } from "client/Features/Hub/Events/ListItem/EventListItem";

export type SpotlightItemSelectorProps = {
	attachable: IValueInputAttachable<number | null>;
	spotlightItemState: ComboBoxIntermediateState<number>;
	kind: HubItemKind;
	timezone: TimeZone;
	isSelectedItemNotAvailable: boolean;
};

export const SpotlightItemSelector = (props: SpotlightItemSelectorProps): ReactElement => {
	const {
		attachable,
		spotlightItemState,
		kind,
		timezone,
		isSelectedItemNotAvailable
	} = props;

	const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

	const debouncedSearchTerm: string | undefined = useDebouncedValue(searchTerm, 300);

	const {
		currentItems,
		getLoadingState
	} = useSpotlightItemDataSource({
		filtering: kind,
		searchTerm: debouncedSearchTerm ?? ""
	});

	const suggestedItems = useMemo(
		(): readonly SuggestedComboBoxItem<number | null, HubItemPreviewModel>[] => {
			return currentItems.map(
				data => ({
					value: data.model.id,
					text: data.model.title,
					highlight: getHighlight(data.model.title, searchTerm),
					data: data
				})
			);
		},
		[currentItems, searchTerm]
	);

	const label: string = localize(itemKindToSingularLocalizationKeyMap[kind]);

	const hasValidationError: boolean = !attachable.validation
		.isValid();

	const renderValidationError = (): ReactElement => {
		if (!hasValidationError && isSelectedItemNotAvailable) {
			return (
				<ValidationError
					{...attachable.attachToValidationError()}
					message={localize("_TheItemYouHadSelectedNoLongerExistOrYouNoLongerHaveAccessToIt")}
				/>
			);
		}

		return <ValidationError {...attachable.attachToValidationError()} />;
	};

	return (
		<FormItem
			label={label}
		>
			<ComboBox
				{...attachable
					.attachToComboBox(spotlightItemState, null)
				}
				isInvalid={hasValidationError || isSelectedItemNotAvailable}
				searchTerm={searchTerm}
				onSearch={setSearchTerm}
				renderSuggestionBox={
					(props, ref) => (
						<ComboBox.SuggestionBox
							baseProps={props}
							menuTitle={label}
							suggestedItems={suggestedItems}
							isLoading={getLoadingState() === "loading"}
							ref={ref}
						>
							{(item, onClick) => (
								<Suggestion
									item={item.data}
									timezone={timezone}
									highlight={item.highlight}
									onClick={onClick}
								/>
							)}
						</ComboBox.SuggestionBox>
					)
				}
			/>
			{renderValidationError()}
		</FormItem>
	);
};
SpotlightItemSelector.displayName = "SpotlightItemSelector";


type SuggestionProps = {
	item: HubItemPreviewModel;
	highlight?: Highlight;
	timezone: TimeZone;
	onClick: () => void;
};

const Suggestion = (props: SuggestionProps): ReactElement => {
	const {
		item,
		highlight,
		timezone,
		onClick
	} = props;

	switch (item.kind) {
		case HubItemKind.Announcement:
			const announcement: AnnouncementPreviewItemModel = item.model;

			return (
				<AnnouncementListItem
					title={announcement.title}
					authorName={announcement.authorName}
					categories={announcement.categories}
					media={announcement.media}
					publicationStart={announcement.publicationStart}
					timezone={timezone}
					trackingId={generateTrackingId("announcement", `|${item.model.id}`)}
					highlights={{ title: highlight }}
					onClick={onClick}
				/>
			);
		case HubItemKind.KnowledgeItem:
			const knowledgeItem: KnowledgeItemPreviewItemModel = item.model;
			return (
				<KnowledgeItemListItem
					title={knowledgeItem.title}
					categoryName={knowledgeItem.category?.name}
					authorName={knowledgeItem.author?.name}
					lastModifiedDate={knowledgeItem.lastModifiedDate}
					media={knowledgeItem.media}
					timezone={timezone}
					trackingId={generateTrackingId("knowledge-item", `|${item.model.id}`)}
					highlights={{ title: highlight }}
					onClick={onClick}
				/>
			);
		case HubItemKind.Event:
			const event: EventPreviewModel = item.model;
			return (
				<EventListItem
					title={event.title}
					authorName={event.author?.name}
					categoryName={event.category?.name}
					media={event.media}
					startDate={event.startDate}
					timezone={timezone}
					trackingId={generateTrackingId("event", `|${item.model.id}`)}
					highlights={{ title: highlight }}
					onClick={onClick}
				/>
			);
		default:
			never(item);
	}
};
Suggestion.displayName = "Suggestion";

type HubItemPreviewModel =
	| {
		kind: HubItemKind.Announcement;
		model: AnnouncementPreviewItemModel;
	}
	| {
		kind: HubItemKind.KnowledgeItem;
		model: KnowledgeItemPreviewItemModel;
	}
	| {
		kind: HubItemKind.Event;
		model: EventPreviewModel;
	};

const useSpotlightItemDataSource = createRemoteDataSource<HubItemPreviewModel, HubItemKind>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true
	})
	.getHook({
		performRequest: async (options): Promise<readonly HubItemPreviewModel[]> => {
			const {
				filterState: itemKind,
				searchTerm,
				signal
			} = options;
			switch (itemKind) {
				case HubItemKind.Announcement:
					return await searchAnnouncements(searchTerm, signal);
				case HubItemKind.KnowledgeItem:
					return await searchKnowledgeItems(searchTerm, signal);
				case HubItemKind.Event:
					return await searchEvents(searchTerm, signal);
				default:
					never(itemKind);
			}
		}
	});

async function searchAnnouncements(searchTerm: string, signal: AbortSignal): Promise<readonly HubItemPreviewModel[]> {
	const response: LazyLoadPageResult<AnnouncementPreviewItemModel> = await listAnnouncements(
		{
			searchTerm: isNilOrWhiteSpace(searchTerm)
				? null
				: searchTerm,
			sortOrder: {
				property: AnnouncementSortProperty.StartTime,
				direction: SortDirection.Descending
			},
			amount: 10
		},
		signal
	);

	return response.items
		.map(model => ({
			kind: HubItemKind.Announcement,
			model
		}));
}

async function searchKnowledgeItems(searchTerm: string, signal: AbortSignal): Promise<readonly HubItemPreviewModel[]> {
	const response: LazyLoadPageResult<KnowledgeItemPreviewItemModel> = await listKnowledgeItems(
		{
			searchTerm: isNilOrWhiteSpace(searchTerm)
				? null
				: searchTerm,
			order: {
				property: KnowledgeItemSortProperty.PublicationDate,
				direction: SortDirection.Descending
			},
			amount: 10
		},
		signal
	);

	return response.items
		.map(model => ({
			kind: HubItemKind.KnowledgeItem,
			model
		}));
}

async function searchEvents(searchTerm: string, signal: AbortSignal): Promise<readonly HubItemPreviewModel[]> {
	const response: LazyLoadPageResult<EventPreviewModel> = await listEvents(
		{
			searchTerm: isNilOrWhiteSpace(searchTerm)
				? null
				: searchTerm,
			sortDirection: EventDateSortDirection.NearToFar,
			filterType: EventDateFilterType.Upcoming,
			amount: 10
		},
		signal
	);

	return response.items
		.map(model => ({
			kind: HubItemKind.Event,
			model
		}));
}

const itemKindToSingularLocalizationKeyMap: { [key in HubItemKind]: LocalizationKey } = {
	[HubItemKind.Announcement]: "_Announcement",
	[HubItemKind.KnowledgeItem]: "_KnowledgeArticle",
	[HubItemKind.Event]: "_Event"
};
